import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import Card from 'components/global/Card';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';

import { faCircleCheck, faCloudMoon, faCloudSun, faSunHaze } from '@fortawesome/pro-light-svg-icons';

import InfoLabel from './component/InfoLabel';
import * as S from './styled/DeliverySlotSelection.styled';

export const deliverySlotSelectionDefaultValues = [
	{
		type: 'color',
		value: '#f5f5f5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#ffffff',
		name: 'formBackgroundColor'
	},

	{
		type: 'color',
		value: '#000000',
		name: 'headerTextColor'
	},

	{
		type: 'color',
		value: '#696969',
		name: 'contentTitleColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'contentTextColor'
	},
	{
		type: 'color',
		value: '#3D5FFF',
		name: 'buttonColor'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	},
	{
		type: 'input',
		value: null,

		name: 'daySelectionTitle'
	},
	{
		type: 'input',
		value: null,

		name: 'daySelectionDescription'
	},
	{
		type: 'input',
		value: null,
		name: 'slotSelectionTitle'
	},
	{
		type: 'input',
		value: null,
		name: 'slotSelectionDescription'
	}
];

const DeliverySlotSelection = (props) => {
	const {
		result: {
			orderId,
			orderItemsArray,
			availableSlotsArray,
			brand,
			deliveryCompanyLogo,
			deliveryCompanyName,
			deliveryCompanyAr,
			orderDate,
			trackingNumber,
			trackingURL,
			status,
			deliverySlotDate,
			deliverySlotFrom,
			deliverySlotTo
		},
		propJson,
		isMobile
	} = props;

	const { showError } = useAlert();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { LanguageCode, getLocaleDateWithTime, getLocaleDate } = useLocale();
	const isMobileView = useMediaQuery(Breakpoints.mobileLandscape);
	const [searchParams] = useSearchParams();
	const [serviceType, setServiceType] = useState(''); // if the service type is courier, we don't need to show the time slots

	const schema = useMemo(
		() =>
			yup.object().shape({
				deliverySlot: yup
					.object()
					.shape({
						text: yup.string().nullable().required(),
						deliverySlotGroupId: yup.number().nullable().required()
					})
					.nullable(),

				deliveryDay: yup.object().nullable().required()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			deliverySlot: null,
			deliveryDay: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isSubmitting, isSubmitSuccessful }
	} = formMethods;

	const selectedDay = watch('deliveryDay');
	const selectedSlot = watch('deliverySlot');

	useEffect(() => {
		if (selectedDay && serviceType !== 'courier') {
			document.getElementById('select-day').scrollIntoView({ behavior: 'smooth' });
		}
	}, [selectedDay, serviceType]);

	const afterConfirmation = async (data) => {
		let startTime = null;
		let endTime = null;

		if (data && data.deliverySlot && data.deliverySlot.text) {
			[startTime, endTime] = data.deliverySlot.text.split(' - ');
		}

		try {
			const response = await openAxios.post(OPEN.SMS.UPDATE_DELIVERY_SLOT_DETAILS, {
				deliverySlotGroupId: data?.deliverySlot?.deliverySlotGroupId ?? null,
				slotId: data?.deliverySlot?.slotId ?? null,
				startTime,
				endTime,
				date: data.deliveryDay.date,
				key: searchParams.get('key')
			});
			if (response.status !== 200) {
				throw new Error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			throw new Error(error);
		}
	};

	const getTimeOfDay = (startTime) => {
		const [hour] = startTime.split(':');
		if (hour >= 6 && hour < 13) return 'morning';
		if (hour >= 13 && hour < 18) return 'afternoon';
		return 'evening';
	};

	const onSubmit = async () => {
		const data = watch();
		let [timeOfDay, startTime, endTime] = [null, null, null];

		if (data?.deliverySlot?.text) {
			[startTime, endTime] = data.deliverySlot.text.split(' - ');
			timeOfDay = getTimeOfDay(startTime);
		}

		NiceModal.show(ConfirmationModal, {
			title: 'confirmDeliverySlot',
			subTitle: (
				<Trans
					i18nKey="confirmDeliverySlotSubTitle"
					values={{
						orderId,
						date: getLocaleDate(data.deliveryDay.date),
						startTime,
						endTime,
						timeOfDay: t(timeOfDay) ?? null
					}}
					components={{
						bold: <S.Bold />
					}}
				/>
			),
			showCloseButton: true,
			rejectLabel: 'cancel',
			confirmLabel: 'confirm',
			// lottieName: 'info-animated-icon',
			yesAction: handleSubmit(afterConfirmation, (errors) => {
				if (errors?.rating || errors?.shipmentRating) {
					showError({
						message: 'errorMsg.choosingDaysRequired'
					});
				}

				AppLogger.log(errors);
			})
		});
	};

	const memoizedItems = useMemo(() => orderItemsArray || [], []);

	const slotItems = useMemo(
		() =>
			availableSlotsArray?.reduce((acc, slot) => {
				const startDate = dayjs(slot.date);
				const day = startDate.format('YYYY-MM-DD');
				let slotData = {};
				let timeOfDay = null;
				let slotTime = null;

				if (slot?.startTime && slot?.endTime) {
					timeOfDay = getTimeOfDay(slot.startTime);
					slotTime = `${slot.startTime} - ${slot.endTime}`;
				}

				slotData = {
					slotId: slot?.slotId ?? null,
					deliverySlotGroupId: slot?.deliverySlotGroupId ?? null,
					capacity: slot?.capacity ?? null,
					text: slotTime
				};

				if (slot && slot.serviceType) {
					setServiceType(slot.serviceType);
					return acc;
				}

				let dayEntry = acc && acc.find((entry) => entry.date === day);
				if (!dayEntry) {
					dayEntry = {
						date: day,
						slots: {
							morning: [],
							afternoon: [],
							evening: []
						}
					};
					acc.push(dayEntry);
				}
				if (!timeOfDay && !slotTime) {
					return acc;
				}

				if (!dayEntry.slots[timeOfDay].includes(slotTime)) {
					dayEntry.slots[timeOfDay].push(slotData);
				}
				return acc;
			}, []),
		[availableSlotsArray]
	);

	const columns = useMemo(
		() => [
			{
				header: 'item',
				accessorKey: 'ItemName',
				size: 120,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<S.ItemInfo>
						<S.ProductImage url={row.original.image} />

						<Typography fontSize="--paragraph3-font-size" fontWeight="500">
							{row.original.itemName}
						</Typography>
					</S.ItemInfo>
				)
			},
			{
				header: 'quantity',
				accessorKey: 'Quantity',
				size: 30,
				maxSize: 30,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<Typography fontSize="--paragraph3-font-size" fontWeight="500" align="center">
						{row.original.quantity}
					</Typography>
				)
			}
		],
		[]
	);

	const handleDayClick = (day) => {
		setValue('deliveryDay', day, { shouldDirty: true, shouldValidate: true });
		setValue('deliverySlot', null, { shouldDirty: true, shouldValidate: true });
	};

	const handleSlotClick = (slot) => {
		setValue('deliverySlot', slot, { shouldDirty: true, shouldValidate: true });
		document.getElementById('confirm-form').scrollIntoView({ behavior: 'smooth' });
	};

	const handleClose = () => {
		navigate(PATHS.BASE);
	};

	return (
		<FormProvider {...formMethods}>
			<S.Container>
				{status || isSubmitSuccessful ? (
					<Card padding={0} background border={0} shadowed rounded>
						<S.SuccessWrapper>
							<Icon icon={faCircleCheck} color="#1CC85F" fontSize="11rem" />
							<Typography
								variant="h4"
								fontWeight="700"
								className="mb-2 mt-4"
								color={propJson?.headerTextColor}>
								yourRequestReceived
							</Typography>
							<Typography
								variant="--paragraph3-font-size"
								className="mb-2 mt-4"
								color={propJson?.headerTextColor}>
								<Trans
									i18nKey="yourOrderWillBeDeliveredWithProps"
									values={{
										orderId,
										date: getLocaleDate(status ? deliverySlotDate : selectedDay?.date),
										slot: status ? `${deliverySlotFrom} - ${deliverySlotTo}` : selectedSlot?.text
									}}
								/>
							</Typography>
							<Button variant="transparent" label="closePage" onClick={handleClose} />
						</S.SuccessWrapper>
					</Card>
				) : (
					<Card padding={0} background border={0} shadowed rounded>
						<S.Wrapper>
							<InfoLabel
								label="orderInformation"
								value={propJson?.showOrderNumber ? `(${orderId})` : ''}
								valueFontWeight="500"
								labelFontWeight="700"
								labelVariant="h4"
								rowMode={!isMobile && !isMobileView}
								labelColor={propJson?.headerTextColor}
								valueColor={propJson?.headerTextColor}
								valueVariant="p1"
								iconColor={propJson?.iconColor}
							/>
							<S.Info className={isMobile ? 'mobileView' : ''}>
								<InfoLabel
									label="storeNameTitle"
									value={brand}
									labelFontWeight="500"
									labelVariant="p2"
									labelColor={propJson?.contentTitleColor}
									valueColor={propJson?.contentTextColor}
									valueFontWeight="500"
									valueVariant="p1"
									className="infoLabel"
								/>
								<S.InfoCustomWrapper>
									<Typography fontWeight="500" variant="p2" color={propJson?.contentTitleColor}>
										deliveryCompany
									</Typography>
									<S.InfoCustomInfo>
										<S.InfoImage>
											{deliveryCompanyLogo && <Image alt="logo" url={deliveryCompanyLogo} />}
										</S.InfoImage>
										<S.InfoContent>
											<Typography
												color={propJson?.contentTextColor}
												fontWeight="600"
												variant="p2">
												{LanguageCode === 'arabicValue' && deliveryCompanyAr
													? deliveryCompanyAr
													: deliveryCompanyName}
											</Typography>

											<S.Link href={trackingURL} target="_blank" rel="noreferrer">
												<Typography variant="p2" noWrap fontWeight="700" color="var(--blue)">
													{trackingNumber}
												</Typography>
											</S.Link>
										</S.InfoContent>
									</S.InfoCustomInfo>
								</S.InfoCustomWrapper>

								<InfoLabel
									label="orderDate"
									value={getLocaleDateWithTime(orderDate)}
									labelFontWeight="500"
									labelVariant="p2"
									labelColor={propJson?.contentTitleColor}
									valueColor={propJson?.contentTextColor}
									valueFontWeight="500"
									valueVariant="p1"
									className="infoLabel"
								/>
							</S.Info>
							<S.Line />
							<S.Accordion header="orderItems" defaultVisible={true}>
								<div>
									<S.AccordionInner>
										<Table columns={columns} data={memoizedItems} />
									</S.AccordionInner>
								</div>
							</S.Accordion>
							<S.Line />
							<S.SlotSelectionContainer>
								<Typography variant="p1" fontWeight="700" color={propJson?.headerTextColor}>
									{propJson?.daySelectionTitle ?? 'selectDay'}
								</Typography>
								<Typography
									variant="caption"
									fontSize="var(--paragraph3-font-size)"
									className="mb-2 mt-3"
									color={propJson?.headerTextColor}>
									{propJson?.daySelectionDescription ?? 'selectDayDescription'}
								</Typography>
								<S.DayContainer>
									{slotItems?.map((item, index) => (
										<DayBoxOption
											key={`day-${index}`}
											daySlot={item}
											selectedDay={selectedDay}
											serviceType={serviceType}
											handleDayClick={handleDayClick}
										/>
									))}
								</S.DayContainer>
								{selectedDay && serviceType !== 'courier' && (
									<S.SelectTimeContainer id="select-day">
										<Typography variant="p1" fontWeight="700" color={propJson?.headerTextColor}>
											{propJson?.slotSelectionTitle ?? 'selectTime'}
										</Typography>

										<Typography
											variant="caption"
											fontSize="var(--paragraph3-font-size)"
											className="mb-2 mt-3"
											color={propJson?.headerTextColor}>
											{propJson?.slotSelectionDescription ?? 'selectTimeDescription'}
										</Typography>

										{selectedDay && selectedDay.slots?.morning?.length > 0 && (
											<TimeSlotsOptions
												period="morningSlots"
												icon={faCloudSun}
												slots={selectedDay.slots.morning}
												handleSelect={handleSlotClick}
												selectedSlot={selectedSlot}
											/>
										)}

										{selectedDay && selectedDay.slots?.afternoon?.length > 0 && (
											<TimeSlotsOptions
												period="afternoonSlots"
												icon={faSunHaze}
												slots={selectedDay.slots.afternoon}
												handleSelect={handleSlotClick}
												selectedSlot={selectedSlot}
											/>
										)}

										{selectedDay && selectedDay.slots?.evening?.length > 0 && (
											<TimeSlotsOptions
												period="eveningSlots"
												icon={faCloudMoon}
												slots={selectedDay.slots.evening}
												handleSelect={handleSlotClick}
												selectedSlot={selectedSlot}
											/>
										)}
									</S.SelectTimeContainer>
								)}
							</S.SlotSelectionContainer>
							<S.FormRow id="confirm-form">
								<Button
									label="confirm"
									variant="blue"
									fullWidth
									onClick={onSubmit}
									disabled={
										selectedDay === null || (serviceType !== 'courier' && selectedSlot === null)
									}
									loading={isSubmitting}
									size="xx-large"
								/>
							</S.FormRow>
						</S.Wrapper>
					</Card>
				)}
			</S.Container>
		</FormProvider>
	);
};

const DayBoxOption = ({ daySlot, selectedDay, handleDayClick, serviceType }) => {
	const slotQty = useMemo(
		() =>
			Object.keys(daySlot.slots)
				.map((day) => daySlot.slots[day].filter((slot) => slot.capacity > 0).length)
				.reduce((acc, curr) => acc + curr, 0),
		[daySlot]
	);
	const isSelected = useMemo(() => selectedDay?.date === daySlot.date, [selectedDay, daySlot]);
	const isDisabled = useMemo(() => slotQty < 1 && serviceType !== 'courier', [slotQty]);

	const isToday = useCallback((date) => dayjs().isSame(date, 'day'), []);

	const generateDayTitle = (date) => (isToday(date) ? 'today' : date.format('ddd'));

	return (
		<S.DayBox
			disabled={isDisabled}
			onClick={() => {
				handleDayClick(daySlot);
			}}
			selected={isSelected}>
			<Typography
				fontSize="var(--paragraph1-font-size)"
				fontWeight="700"
				color={isDisabled ? 'var(--content-4)' : 'var(--black)'}>
				{generateDayTitle(dayjs(daySlot.date))}
			</Typography>
			<Typography
				fontSize="var(--paragraph2-font-size)"
				color={isDisabled ? 'var(--content-4)' : 'var(--content-2)'}>
				{dayjs(daySlot.date).format('DD MMM')}
			</Typography>
			{serviceType !== 'courier' && (
				<S.DayBadge disabled={isDisabled} selected={isSelected}>
					<Typography
						noWrap={false}
						variant="--caption-font-size"
						color={isDisabled ? 'var(--content-2)' : isSelected ? 'var(--white)' : 'var(--blue)'}>
						<Trans
							i18nKey="availableSlots"
							values={{
								slot: slotQty ?? 0
							}}
						/>
					</Typography>
				</S.DayBadge>
			)}
		</S.DayBox>
	);
};

const TimeSlotsOptions = ({ period, icon, slots, handleSelect, selectedSlot }) => (
	<S.DaySlotContainer>
		<S.TitleBox>
			<Icon icon={icon} fontSize="var(--heading4-font-size)" color="var(--content-2)" />
			<S.SlotTitle
				variant="caption"
				fontSize="var(--paragraph2-font-size)"
				color="var(--content-2)"
				fontWeight="500"
				className="mb-2 mt-4 mx-3">
				{period}
			</S.SlotTitle>
			<S.BasicLine />
		</S.TitleBox>
		<S.SlotContainer>
			{slots?.map((slot) => (
				<S.SlotBox
					key={slot.text}
					onClick={() => handleSelect(slot)}
					selected={selectedSlot?.text === slot.text}
					disabled={slot.capacity === 0}>
					<Typography fontSize="var(--paragraph2-font-size)" fontWeight="500">
						{slot.text}
					</Typography>
				</S.SlotBox>
			))}
		</S.SlotContainer>
	</S.DaySlotContainer>
);

export default DeliverySlotSelection;
